import React, { useState, useEffect } from 'react';
import { grantsService, organizationsService } from '../services/api';
import './GrantForm.css';

const GrantForm = ({ initialData = null, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState(initialData ? {
    name: initialData.name || '',
    organization_id: initialData.organization_id || '',
    amount_requested: initialData.amount_requested || '',
    is_in_kind: initialData.is_in_kind || false,
    date_applied: initialData.date_applied ? initialData.date_applied.split('T')[0] : new Date().toISOString().split('T')[0],
    status: initialData.status || 'in_progress',
    description: initialData.description || ''
  } : {
    name: '',
    organization_id: '',
    amount_requested: '',
    is_in_kind: false,
    date_applied: new Date().toISOString().split('T')[0],
    status: 'in_progress',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    setLoadingOrgs(true);
    try {
      const response = await organizationsService.list({ limit: 999999 });
      setOrganizations(response.data.data || []);
    } catch (err) {
      console.error('Error fetching organizations:', err);
    } finally {
      setLoadingOrgs(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: newValue
      };
      
      // If toggling to in-kind, set amount to 0
      if (name === 'is_in_kind' && checked) {
        updated.amount_requested = 0;
      }
      
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      setError('Grant name is required');
      return;
    }

    if (!formData.organization_id) {
      setError('Organization is required');
      return;
    }

    // Only validate amount for monetary grants
    if (!formData.is_in_kind) {
      if (!formData.amount_requested || formData.amount_requested <= 0) {
        setError('Grant amount must be greater than 0');
        return;
      }
    }

    if (!formData.date_applied) {
      setError('Application date is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare data to submit
      const submitData = {
        ...formData,
        amount_requested: formData.is_in_kind ? 0 : formData.amount_requested
      };
      
      if (initialData?.id) {
        await grantsService.update(initialData.id, submitData);
      } else {
        await grantsService.create(submitData);
      }
      
      if (onSubmit) onSubmit();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save grant');
      console.error('Error saving grant:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="form-header">
          <h3>{initialData ? 'Edit Grant' : 'New Grant'}</h3>
          <button 
            className="close-btn"
            onClick={onCancel}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit} className="form">
          <div className="form-section">
            <h4>Grant Information</h4>

            <div className="form-group">
              <label htmlFor="name">Grant Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Community Development Grant"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="organization_id">Organization *</label>
              <select
                id="organization_id"
                name="organization_id"
                value={formData.organization_id}
                onChange={handleChange}
                required
                disabled={loading || loadingOrgs}
              >
                <option value="">Select an organization</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  name="is_in_kind"
                  checked={formData.is_in_kind}
                  onChange={handleChange}
                  disabled={loading}
                />
                <span>Is In Kind</span>
              </label>
              <p className="field-hint">Check this if the grant provides goods or services instead of money</p>
            </div>

            {!formData.is_in_kind && (
              <div className="form-group">
                <label htmlFor="amount_requested">Grant Amount *</label>
                <input
                  type="number"
                  id="amount_requested"
                  name="amount_requested"
                  step="0.01"
                  min="0"
                  value={formData.amount_requested}
                  onChange={handleChange}
                  placeholder="0.00"
                  required
                  disabled={loading}
                />
              </div>
            )}
          </div>

          <div className="form-section">
            <h4>Application Details</h4>

            <div className="form-group">
              <label htmlFor="date_applied">Application Date *</label>
              <input
                type="date"
                id="date_applied"
                name="date_applied"
                value={formData.date_applied}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="applied">Applied</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="in_progress">In Progress</option>
                <option value="funded">Funded</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Grant description and details..."
                rows="4"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="button"
              className="btn btn-outline"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (initialData ? 'Update' : 'Create')} Grant
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GrantForm;
